import type { FeatureContext } from './featureContext';
import { initAdminPanel } from '../adminPanel';
import { adminMountBody } from '../adminMounts';

/** Injectable seams for unit tests; defaults are the real modules. */
export interface AdminInstallDeps {
  initAdminPanel?: typeof initAdminPanel;
}

/**
 * The admin panel (ADR-0183): built pre-login like the toolbar panels, so a
 * reconnect that flips admin on/off just calls `setAvailable`. Reveal-only - the
 * callbacks send server commands the server re-authorizes against the socket's own
 * passcode-proven identity, so wiring the panel grants no power. The teleport
 * cursor / no-clip movement mirror live in the bootstrap's movement core (they are
 * cursor/prediction state); this installer drives them through the shared-state
 * seams and registers the F10 toggle + the rebindable `admin-teleport` action.
 */
export function installAdmin(ctx: FeatureContext, deps: AdminInstallDeps = {}): void {
  const tunedRiderHeights = new Map<
    number,
    { movingOffsetY?: number; idleOffsetY?: number }
  >();
  const adminPanel = (deps.initAdminPanel ?? initAdminPanel)({
    onToggleNoClip: (enabled) => ctx.state.setNoClip(enabled),
    onArmTeleport: () => ctx.state.armTeleport(),
    onSetSize: (size) => ctx.client()?.setAdminSize(size),
    onStartCombatLab: (scenario) => {
      if (!ctx.state.isAdminViewer()) return;
      ctx.chatCommands.handle(`/dungeon combat ${scenario}`);
    },
    onEquipBascinet: () => ctx.client()?.adminEquipBascinet(),
    // The panel names a mount choice; this adapter maps it to the registered body id.
    // The server re-authorizes and owns the replicated result.
    onSetMount: (mount) =>
      ctx.client()?.adminSetMount(mount === undefined ? undefined : adminMountBody(mount)),
    onTuneMountRiderHeight: (mount, gait, offsetY) => {
      const body = adminMountBody(mount);
      const current = tunedRiderHeights.get(body) ?? {};
      const next = gait === 'moving'
        ? { ...current, movingOffsetY: offsetY }
        : { ...current, idleOffsetY: offsetY };
      tunedRiderHeights.set(body, next);
      ctx.renderer()?.setMountRiderHeightOverride(body, next);
    },
    onResetMountRiderHeight: (mount) => {
      const body = adminMountBody(mount);
      tunedRiderHeights.delete(body);
      ctx.renderer()?.clearMountRiderHeightOverride(body);
    },
  });

  ctx.onRendererBuilt((renderer) => {
    for (const [body, height] of tunedRiderHeights) {
      renderer.setMountRiderHeightOverride(body, height);
    }
  });

  // Reveal-only admin signal, fired on every Welcome: (un)wire the admin UI to
  // match. This grants NO power - even a panel forced open in devtools is
  // powerless on a non-admin. A reconnect that loses admin re-hides everything
  // (the bootstrap's own isAdmin handler drops the armed teleport cursor and the
  // Settings rebind row).
  ctx.events.on('isAdmin', (admin) => adminPanel.setAvailable(admin));

  // No-clip is session-scoped and default-off server-side: a reconnect starts a
  // fresh session with no-clip off, so reset the panel button to match (the
  // bootstrap resets its own movement mirror + teleport cursor in the same Welcome).
  ctx.events.on('welcome', () => adminPanel.setNoClip(false));

  // Admin teleport: arm the tile-target cursor. Gated on the reveal-only admin
  // flag so a non-admin who somehow has the key bound gets nothing; the server is
  // the real gate (it rejects AdminTeleport for a non-admin regardless).
  ctx.actions.register('admin-teleport', {
    down: () => {
      if (ctx.state.isAdminViewer()) ctx.state.armTeleport();
    },
  });

  // F10 toggles the admin panel - a fixed, non-rebindable panel key (like the
  // toolbar buttons), so it is NOT a keybindings action. Gated on the reveal-only
  // admin flag: for an admin it swallows F10 and slides the panel; for everyone
  // else it falls through inert (F10 has no game meaning). While a text field is
  // focused the visitor is typing, so the key must reach the field.
  ctx.ui.defaultView?.addEventListener('keydown', (e) => {
    if (e.code !== 'F10' || !ctx.state.isAdminViewer()) return;
    const el = ctx.ui.activeElement;
    const typing =
      el instanceof HTMLElement &&
      (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
    if (typing) return;
    e.preventDefault();
    adminPanel.toggle();
  });
}
